/**
 * Pure Jimp image operations — the implementations behind `image-utils.ts`.
 *
 * Split out from `image-utils.ts` so the CPU-offload child can import the work
 * without importing the routing façade (which would drag the pool, the gate, and
 * the snapshot formatter into a child bundle that needs none of them).
 *
 * **Everything here MUST stay a pure function of its JSON-serializable request:**
 * `jimp` is the only import, there is no I/O, no page access, and no shared
 * mutable state. Inputs and outputs are base64 strings, which is what lets these
 * cross the worker-pool's UTF-8 JSON frame boundary unchanged — no binary
 * transport, unlike the audio path (spec §13-T2b).
 *
 * Option defaults are resolved by the CALLER (`image-utils.ts`) before the
 * request is built, so the child never re-derives a default and the inline and
 * offloaded paths are identical by construction.
 *
 * @module image-ops
 */

import { Jimp } from 'jimp';

export const MAX_DIMENSION = 1280;
const JPEG_QUALITY = 70;
/** Annotation pass encodes slightly higher — marker edges suffer at 70. */
const ANNOTATE_JPEG_QUALITY = 80;

// Coordinate grid overlay constants
export const DEFAULT_GRID_SPACING = 100; // pixels in original viewport coordinates
export const DEFAULT_MARGIN_SIZE = 32; // pixels for coordinate labels
const GRID_LINE_COLOR = 0xc8c8c866; // Light gray with ~40% opacity (rgba 200,200,200,0.4)

/** Default highlight color: bright red with full opacity */
export const DEFAULT_HIGHLIGHT_COLOR = 0xff0000ff;
export const DEFAULT_BORDER_WIDTH = 3;
export const ANNOTATION_BORDER_WIDTH = 2;

/**
 * Bounding box for element highlighting.
 */
export interface BoundingBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Pixel dimensions of a viewport or source image. */
export interface Dimensions {
  width: number;
  height: number;
}

/**
 * Indexed annotation for search result highlighting.
 */
export interface IndexedAnnotation {
  /** 1-based index (1-10) for color mapping */
  index: number;
  /** Bounding box of the element */
  box: BoundingBox;
}

/** A Jimp instance, named without re-importing Jimp's internal types. */
type JimpImage = Awaited<ReturnType<typeof Jimp.read>>;

// Color palette for indexed markers (high contrast, distinct colors with ~70% opacity)
// Each index (1-10) gets a unique color that matches the frontmatter legend
// Format: 0xRRGGBBAA where AA is alpha (B0 = ~70% opacity)
const MARKER_COLORS = [
  0xff0000b0, // 1: Red
  0x00ff00b0, // 2: Green
  0x0066ffb0, // 3: Blue
  0xffff00b0, // 4: Yellow
  0xff00ffb0, // 5: Magenta
  0x00ffffb0, // 6: Cyan
  0xff8800b0, // 7: Orange
  0x8800ffb0, // 8: Purple
  0x00ff88b0, // 9: Teal
  0xff0088b0, // 10: Pink
];

/**
 * One image operation, fully resolved — every option carries a concrete value so
 * the executing side (host or child) makes no decisions of its own.
 */
export type ImageOpRequest =
  | { op: 'downsample'; base64: string; maxDimension: number }
  | {
      op: 'crop';
      base64: string;
      box: BoundingBox;
      viewportSize: Dimensions;
      padding: number;
    }
  | {
      op: 'highlight';
      base64: string;
      box: BoundingBox;
      color: number;
      borderWidth: number;
    }
  | {
      op: 'annotate';
      base64: string;
      annotations: IndexedAnnotation[];
      borderWidth: number;
      /** Original viewport dimensions before downsampling; boxes scale to match. */
      originalDimensions?: Dimensions;
    }
  | {
      op: 'grid';
      base64: string;
      originalDimensions: Dimensions;
      gridSpacing: number;
      marginSize: number;
    };

export interface ImageOpResult {
  /** Result image, base64-encoded (JPEG for every op). */
  base64: string;
}

/**
 * The wire form of a result: the operation's own failure is reported as data,
 * not as a rejection.
 *
 * The pool surfaces a task's throw as the same `CpuOffloadWorkerError` it uses
 * for a crashed or unspawnable child, so a *deterministic* input failure — an
 * undecodable image, which the AI filesystem tool can genuinely produce by
 * reading an arbitrary on-disk file — would otherwise be counted as a
 * `runner_fault` (an alarmed series) and pointlessly re-run inline to fail the
 * same way. Returning it keeps the fault taxonomy about the pool's health.
 */
export type ImageOpOutcome =
  | ({ ok: true } & ImageOpResult)
  | { ok: false; message: string; name?: string };

/**
 * Execute one resolved image operation. The single entry point, so the offload
 * task and the inline fallback dispatch through identical code.
 */
export async function runImageOp(request: ImageOpRequest): Promise<ImageOpResult> {
  switch (request.op) {
    case 'downsample':
      return { base64: await downsample(request) };
    case 'crop':
      return { base64: await crop(request) };
    case 'highlight':
      return { base64: await highlight(request) };
    case 'annotate':
      return { base64: await annotate(request) };
    case 'grid':
      return { base64: await grid(request) };
  }
}

/**
 * {@link runImageOp} with its own failure captured as data — the body of the
 * `image-op` task. See {@link ImageOpOutcome} for why.
 */
export async function runImageOpCaptured(request: ImageOpRequest): Promise<ImageOpOutcome> {
  try {
    return { ok: true, ...(await runImageOp(request)) };
  } catch (err) {
    // Carry `name` alongside the message (as the pool's own error frame does) so
    // the host can rebuild an error that reads like the inline path's — the knob
    // should change where the work runs, not what a failure looks like.
    if (err instanceof Error) return { ok: false, message: err.message, name: err.name };
    return { ok: false, message: String(err) };
  }
}

/** Decode raw base64 (no data-URL prefix) into a Jimp image. */
async function read(base64Data: string): Promise<JimpImage> {
  return Jimp.read(Buffer.from(base64Data, 'base64'));
}

/**
 * Downsample an image to fit within `maxDimension` while preserving aspect ratio.
 * Returns a JPEG base64 string optimized for AI vision models.
 */
async function downsample(req: Extract<ImageOpRequest, { op: 'downsample' }>): Promise<string> {
  const image = await read(req.base64);
  const { width, height } = image;

  // Only resize if larger than maxDimension
  if (width > req.maxDimension || height > req.maxDimension) {
    // Determine scale factor to fit within maxDimension
    const scale = Math.min(req.maxDimension / width, req.maxDimension / height);
    image.resize({ w: Math.round(width * scale), h: Math.round(height * scale) });
  }

  // Convert to JPEG with specified quality
  const outputBuffer = await image.getBuffer('image/jpeg', { quality: JPEG_QUALITY });
  return outputBuffer.toString('base64');
}

/**
 * Crop a full-page screenshot to a bounding box region.
 * Used for coordinate-based dry-runs where locator.screenshot() isn't available.
 */
async function crop(req: Extract<ImageOpRequest, { op: 'crop' }>): Promise<string> {
  const image = await read(req.base64);
  const { width: imgW, height: imgH } = image;

  // Scale bounding box from viewport coordinates to image coordinates
  const scaleX = imgW / req.viewportSize.width;
  const scaleY = imgH / req.viewportSize.height;

  const cropX = Math.max(0, Math.round(req.box.x * scaleX - req.padding));
  const cropY = Math.max(0, Math.round(req.box.y * scaleY - req.padding));
  const cropW = Math.min(imgW - cropX, Math.round(req.box.width * scaleX + req.padding * 2));
  const cropH = Math.min(imgH - cropY, Math.round(req.box.height * scaleY + req.padding * 2));

  image.crop({ x: cropX, y: cropY, w: cropW, h: cropH });

  const outputBuffer = await image.getBuffer('image/jpeg', { quality: JPEG_QUALITY });
  return outputBuffer.toString('base64');
}

/**
 * Draw a highlight rectangle around an element on a screenshot.
 * The highlight is drawn as a colored border around the bounding box.
 */
async function highlight(req: Extract<ImageOpRequest, { op: 'highlight' }>): Promise<string> {
  const image = await read(req.base64);
  drawBoxBorder(image, req.box, req.color, req.borderWidth);

  // Return as JPEG for smaller size (consistent with other screenshot handling)
  const outputBuffer = await image.getBuffer('image/jpeg', { quality: JPEG_QUALITY });
  return outputBuffer.toString('base64');
}

/**
 * Annotate a screenshot with indexed color-coded bounding boxes.
 * Each index (1-10) gets a unique color. The frontmatter contains the legend
 * mapping index→ref→element info.
 */
async function annotate(req: Extract<ImageOpRequest, { op: 'annotate' }>): Promise<string> {
  const image = await read(req.base64);

  // Calculate scale factor if original dimensions provided
  const scaleX = req.originalDimensions ? image.width / req.originalDimensions.width : 1;
  const scaleY = req.originalDimensions ? image.height / req.originalDimensions.height : 1;

  for (const { index, box } of req.annotations) {
    const color = MARKER_COLORS[(index - 1) % MARKER_COLORS.length];

    // Scale box coordinates to match downsampled image
    const scaledBox: BoundingBox = {
      x: box.x * scaleX,
      y: box.y * scaleY,
      width: box.width * scaleX,
      height: box.height * scaleY,
    };

    // Draw colored border around element (no fill, just outline)
    drawBoxBorder(image, scaledBox, color, req.borderWidth);
  }

  const outputBuffer = await image.getBuffer('image/jpeg', { quality: ANNOTATE_JPEG_QUALITY });
  return outputBuffer.toString('base64');
}

/**
 * Draw a colored border around a bounding box.
 */
function drawBoxBorder(
  image: JimpImage,
  box: BoundingBox,
  color: number,
  borderWidth: number
): void {
  const imgWidth = image.width;
  const imgHeight = image.height;

  // Clamp box coordinates to image bounds
  const x1 = Math.max(0, Math.floor(box.x));
  const y1 = Math.max(0, Math.floor(box.y));
  const x2 = Math.min(imgWidth - 1, Math.floor(box.x + box.width));
  const y2 = Math.min(imgHeight - 1, Math.floor(box.y + box.height));

  // Draw the four sides of the rectangle border
  // Top edge
  for (let x = x1; x <= x2; x++) {
    for (let t = 0; t < borderWidth && y1 + t < imgHeight; t++) {
      image.setPixelColor(color, x, y1 + t);
    }
  }

  // Bottom edge
  for (let x = x1; x <= x2; x++) {
    for (let t = 0; t < borderWidth && y2 - t >= 0; t++) {
      image.setPixelColor(color, x, y2 - t);
    }
  }

  // Left edge
  for (let y = y1; y <= y2; y++) {
    for (let t = 0; t < borderWidth && x1 + t < imgWidth; t++) {
      image.setPixelColor(color, x1 + t, y);
    }
  }

  // Right edge
  for (let y = y1; y <= y2; y++) {
    for (let t = 0; t < borderWidth && x2 - t >= 0; t++) {
      image.setPixelColor(color, x2 - t, y);
    }
  }
}

// Simple 3x5 pixel font for digits 0-9
// Each digit is represented as a 3x5 grid of 1s and 0s
const DIGIT_PATTERNS: Record<string, number[][]> = {
  '0': [
    [1, 1, 1],
    [1, 0, 1],
    [1, 0, 1],
    [1, 0, 1],
    [1, 1, 1],
  ],
  '1': [
    [0, 1, 0],
    [1, 1, 0],
    [0, 1, 0],
    [0, 1, 0],
    [1, 1, 1],
  ],
  '2': [
    [1, 1, 1],
    [0, 0, 1],
    [1, 1, 1],
    [1, 0, 0],
    [1, 1, 1],
  ],
  '3': [
    [1, 1, 1],
    [0, 0, 1],
    [1, 1, 1],
    [0, 0, 1],
    [1, 1, 1],
  ],
  '4': [
    [1, 0, 1],
    [1, 0, 1],
    [1, 1, 1],
    [0, 0, 1],
    [0, 0, 1],
  ],
  '5': [
    [1, 1, 1],
    [1, 0, 0],
    [1, 1, 1],
    [0, 0, 1],
    [1, 1, 1],
  ],
  '6': [
    [1, 1, 1],
    [1, 0, 0],
    [1, 1, 1],
    [1, 0, 1],
    [1, 1, 1],
  ],
  '7': [
    [1, 1, 1],
    [0, 0, 1],
    [0, 0, 1],
    [0, 0, 1],
    [0, 0, 1],
  ],
  '8': [
    [1, 1, 1],
    [1, 0, 1],
    [1, 1, 1],
    [1, 0, 1],
    [1, 1, 1],
  ],
  '9': [
    [1, 1, 1],
    [1, 0, 1],
    [1, 1, 1],
    [0, 0, 1],
    [1, 1, 1],
  ],
};

/**
 * Draw a number at the specified position using pixel font.
 */
function drawNumber(
  image: JimpImage,
  num: number,
  x: number,
  y: number,
  color: number = 0x000000ff
): void {
  const digits = num.toString().split('');
  let offsetX = x;

  for (const digit of digits) {
    const pattern = DIGIT_PATTERNS[digit];
    if (!pattern) continue;

    for (let row = 0; row < 5; row++) {
      for (let col = 0; col < 3; col++) {
        if (pattern[row][col] === 1) {
          const px = offsetX + col;
          const py = y + row;
          if (px >= 0 && px < image.width && py >= 0 && py < image.height) {
            image.setPixelColor(color, px, py);
          }
        }
      }
    }
    offsetX += 4; // 3 pixels wide + 1 pixel spacing
  }
}

/**
 * Get the pixel width of a number when rendered with our pixel font.
 */
function getNumberWidth(num: number): number {
  const digits = num.toString().length;
  return digits * 4 - 1; // 3 pixels per digit + 1 pixel spacing, minus trailing space
}

/**
 * Add a coordinate grid overlay to a screenshot.
 *
 * Creates a new image with margins for coordinate labels.
 * Grid lines are drawn at specified intervals in ORIGINAL viewport coordinates,
 * but scaled to match the downsampled image dimensions.
 * Labels show original viewport coordinates so clicking coordinates match.
 */
async function grid(req: Extract<ImageOpRequest, { op: 'grid' }>): Promise<string> {
  const { gridSpacing, marginSize } = req;
  const sourceImage = await read(req.base64);

  const sourceWidth = sourceImage.width;
  const sourceHeight = sourceImage.height;

  // Calculate scale factors from original viewport to downsampled image
  const scaleX = sourceWidth / req.originalDimensions.width;
  const scaleY = sourceHeight / req.originalDimensions.height;

  // Create new canvas with margins
  const canvasWidth = sourceWidth + marginSize;
  const canvasHeight = sourceHeight + marginSize;

  // Create white background canvas via Jimp.read for type compatibility
  // First create a temporary white image buffer
  const tempCanvas = new Jimp({ width: canvasWidth, height: canvasHeight, color: 0xffffffff });
  const canvasBuffer = await tempCanvas.getBuffer('image/png');
  const canvas = await Jimp.read(canvasBuffer);

  // Copy source image pixels to canvas at margin offset
  for (let y = 0; y < sourceHeight; y++) {
    for (let x = 0; x < sourceWidth; x++) {
      const pixel = sourceImage.getPixelColor(x, y);
      canvas.setPixelColor(pixel, x + marginSize, y + marginSize);
    }
  }

  // Draw grid lines on the screenshot area (not in margins)
  // Lines are at gridSpacing intervals in original viewport coordinates

  // Vertical grid lines (X coordinates)
  for (let origX = 0; origX <= req.originalDimensions.width; origX += gridSpacing) {
    const scaledX = Math.round(origX * scaleX) + marginSize;
    if (scaledX >= marginSize && scaledX < canvasWidth) {
      // Draw vertical line
      for (let y = marginSize; y < canvasHeight; y++) {
        canvas.setPixelColor(GRID_LINE_COLOR, scaledX, y);
      }

      // Draw label in top margin
      // Center the number above the line
      const labelWidth = getNumberWidth(origX);
      const labelX = Math.max(0, scaledX - Math.floor(labelWidth / 2));
      const labelY = marginSize - 10; // Position above the grid area
      drawNumber(canvas, origX, labelX, labelY, 0x666666ff);

      // Draw small tick mark
      for (let t = 0; t < 3; t++) {
        if (marginSize - 4 + t >= 0) {
          canvas.setPixelColor(0x666666ff, scaledX, marginSize - 4 + t);
        }
      }
    }
  }

  // Horizontal grid lines (Y coordinates)
  for (let origY = 0; origY <= req.originalDimensions.height; origY += gridSpacing) {
    const scaledY = Math.round(origY * scaleY) + marginSize;
    if (scaledY >= marginSize && scaledY < canvasHeight) {
      // Draw horizontal line
      for (let x = marginSize; x < canvasWidth; x++) {
        canvas.setPixelColor(GRID_LINE_COLOR, x, scaledY);
      }

      // Draw label in left margin
      // Right-align the number and vertically center it
      const labelWidth = getNumberWidth(origY);
      const labelX = marginSize - labelWidth - 5;
      const labelY = scaledY - 2; // Vertically center (5px font height / 2)
      if (labelX >= 0) {
        drawNumber(canvas, origY, labelX, labelY, 0x666666ff);
      }

      // Draw small tick mark
      for (let t = 0; t < 3; t++) {
        if (marginSize - 3 + t >= 0 && marginSize - 3 + t < marginSize) {
          canvas.setPixelColor(0x666666ff, marginSize - 3 + t, scaledY);
        }
      }
    }
  }

  // Convert to JPEG
  const outputBuffer = await canvas.getBuffer('image/jpeg', { quality: JPEG_QUALITY });
  return outputBuffer.toString('base64');
}
